import cn from 'classnames';
import Map from 'react-map-gl/mapbox';
import { BsClock } from "react-icons/bs";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import { Link } from 'react-router';

const VIEWPORT = {
  latitude: 37.421999,
  longitude: -122.084057,
  zoom: 14,
  maxZoom: 14,
  minZoom: 14,
};

// Function to extract city from site - prioritize Google Address data
const getCityFromSite = (site: any): string => {
  if (!site) return '';
  
  console.log('🏙️ Getting city for site:', site.name, 'address:', site.address);
  console.log('🏙️ Google address data:', site.google_address_data);
  
  // First, try to use structured Google Address data with new fields
  if (site.google_address_data) {
    const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
    
    console.log('🏙️ Parsed fields:', { city, state, locality, administrative_area_level_1 });
    
    // Use locality (city name) from Google Address Data
    if (locality && administrative_area_level_1) {
      console.log('🏙️ Using locality + admin:', `${locality}, ${administrative_area_level_1}`);
      return `${locality}, ${administrative_area_level_1}`;
    }
    if (locality) {
      console.log('🏙️ Using locality only:', locality);
      return locality;
    }
    
    // Fallback to old fields for backward compatibility
    if (city && state) {
      console.log('🏙️ Using city + state:', `${city}, ${state}`);
      return `${city}, ${state}`;
    }
    if (city) {
      console.log('🏙️ Using city only:', city);
      return city;
    }
  }
  
  // Fallback to parsing the address string (for backward compatibility)
  const address = site.address;
  if (!address) return '';
  
  console.log('🏙️ Fallback parsing address:', address);
  
  // Split address by comma and trim whitespace
  const parts = address.split(',').map((part: string) => part.trim());
  console.log('🏙️ Address parts:', parts);
  
  // Common address formats:
  // "123 Main St, San Francisco, CA 94102" -> "San Francisco"
  // "123 Main St, New York, NY" -> "New York" 
  // "San Francisco, CA" -> "San Francisco"
  
  if (parts.length >= 2) {
    // Look for the city part (usually the second part, but not always)
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      // Skip if it looks like a state code (2 letters) or zip code (numbers)
      if (!/^\d+$/.test(part) && !/^[A-Z]{2}$/.test(part) && part.length > 2) {
        // Additional check: if this part contains a zip code, extract just the city
        const cityPart = part.split(' ')[0]; // Take first word if it contains zip
        if (cityPart && cityPart.length > 2) {
          return cityPart;
        }
        return part;
      }
    }
    // Fallback to second part if no clear city found
    return parts[1];
  }
  
  // If only one part, return it (might be just city name)
  return parts[0];
};

// Function to determine project color based on new rules
const getProjectColor = (project: ProjectInterface): string => {
  // Check if project has no Action Items or Project Plan Items
  const hasNoItems = (!project.items || project.items.length === 0) && 
                     (!(project as any).actionItems || (project as any).actionItems.length === 0);
  
  if (hasNoItems) {
    return 'bg-green-500'; // Green if no items
  }
  
  // Check if all tasks have no risk or low risk
  const allItemsLowRisk = project.items?.every(item => 
    !item.risks || item.risks === null || item.risks.toLowerCase() === 'low' || item.risks.toLowerCase() === 'none'
  ) ?? true;
  
  const allActionItemsLowRisk = (project as any).actionItems?.every((item: any) => 
    !item.priority || item.priority === null || item.priority.toLowerCase() === 'low'
  ) ?? true;
  
  if (allItemsLowRisk && allActionItemsLowRisk) {
    return 'bg-green-500'; // Green if all risks are low or none
  }
  
  // Fall back to original status-based logic
  if (project.status === 'NOT_STARTED') return 'bg-gray-500';
  if (project.status === 'COMPLETED') return 'bg-orange-400';
  if (project.status === 'IN_PROGRESS') return 'bg-red-500';
  
  return 'bg-gray-500'; // Default
};

export const DashboardList = ({ servers }: {servers: ProjectInterface[]}) => (
  <div className="p-4 mx-auto pt-20 bg-gray-100 min-h-screen">
    {servers.map((server) => {
      const defaultSite = server.sites?.find((site: any) => site.is_default) || server.sites?.[0];
      const vp = {
        ...VIEWPORT, 
        ...defaultSite ? {
          latitude: defaultSite.geo_location[0],
          longitude: defaultSite.geo_location[1]
        } : {}
      };
      
      const projectColor = getProjectColor(server);
      
      return (
        <Link
          key={server.id}
          to={`/projects/${server.id}`}
          className='flex items-center mb-4 items-stretch rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer'
        >
          <div
            className={cn('text-white flex justify-center items-center text-md w-8', projectColor)}
          >
            {server.status === 'NOT_STARTED' && <HiOutlineBadgeCheck size={20} />}
            {server.status === 'COMPLETED' && <BsClock size={18} />}
            {server.status === 'IN_PROGRESS' && server.items?.length}
          </div>
          <div
            className={
              cn(
                "marker bg-white flex-1 flex justify-start items-center p-4 gap-1",
              )
            }
          >
            <div className='leading-none pr-2'>
              <div className='text-xl'>{server.name}</div>
                                      <div>{getCityFromSite(defaultSite)}</div>

              <div className='mt-6'>{server.items?.length} items</div>
              <div>{server.users?.length} users</div>

              <div className='mt-6 font-semibold'>{server.phases?.length} phases</div>
            </div>
          </div>
          <div className='w-100'>
            <Map
              initialViewState={vp}
              style={{width: '100%', height: '200px'}}
              mapboxAccessToken='pk.eyJ1IjoiZWlnaHR5ZGF5cyIsImEiOiJjam9zYXBiZGswb2NtM2pwdHY5eGZmcGR5In0.lJpch0oIthk_eye7jJnq5A'
              mapStyle="mapbox://styles/eightydays/cjosb2zt801sy2ro3pozm9nn6"
            />
          </div>
        </Link>
      )
    })}
  </div>
);